/* Store builder kit · quantity-ladder prices in a non-shop currency.
   The ladder (theme setting sb_qty_offers) holds fixed amounts in the SHOP currency; Shopify converts them with the
   presentment rate at checkout. Liquid can't see that rate, so a fixed "2:14.99" subtracted from a EUR price shows the
   wrong total (EUR 76.91 shown, 78.37 charged). Sections that print ladder prices outside the product page (home bundle
   cards, cart-drawer upsell) wrap them in <sb-currency-pricing> and keep the Liquid price hidden while
   cart.currency != shop.currency; this element recomputes them with window.Shopify.currency.rate, then shows them.
   Same rules as snippets/sb-qty-off.liquid and qtyOff in sb-pdp.js (percentages floor to the cent like Shopify).

   data-kind="home"    data-units, data-subtotal (count x unit price, presentment cents), data-money-format,
                       data-each-template ("[price] each"); fills [data-sb-money-total] and [data-sb-money-each].
   data-kind="upsell"  data-units (in cart), data-prices (the cart's unit prices, comma list), data-next-units (the upsell target: +1 or the next sb_qty_steps count),
                       data-single, data-subtotal, data-next-subtotal; fills [data-sb-money-extra] and
                       [data-sb-money-saving] with the Liquid rules: extra = price(next) - price(now),
                       saving = single x (next - now) - extra. The card stays hidden when that saves nothing.
   Then every [data-sb-price-ready] inside is shown. Cart re-renders are picked up by a MutationObserver. */

function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const fmt = format || '${{amount}}';
  const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision).split('.');
    const whole = fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (fixed[1] ? decimal + fixed[1] : '');
  };
  const key = (fmt.match(placeholder) || [])[1] || 'amount';
  const table = {
    amount: () => withDelimiters(cents, 2),
    amount_no_decimals: () => withDelimiters(cents, 0),
    amount_with_comma_separator: () => withDelimiters(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => withDelimiters(cents, 0, '.', ','),
    amount_with_space_separator: () => withDelimiters(cents, 2, ' ', ','),
    amount_no_decimals_with_space_separator: () => withDelimiters(cents, 0, ' ', ','),
    amount_with_apostrophe_separator: () => withDelimiters(cents, 2, "'", '.'),
  };
  const value = (table[key] || table.amount)();
  return fmt.replace(placeholder, value).replace(/<[^>]*>/g, '').trim();
}

function qtyOff(ladder, units, subtotal, prices) {
  let best = null;
  String(ladder || '').split(',').forEach((step) => {
    const [n, v] = step.split(':');
    const q = parseInt(n, 10);
    if (q > 0 && q <= units && v && v.trim() && (!best || q >= best.q)) best = { q, v: v.trim() };
  });
  if (!best) return 0;
  const rate = Number(window.Shopify?.currency?.rate) || 1;
  // percentages floor per unit like Shopify (2 x 29.95 at 10% = 5.98 off), so pass the unit prices
  const pct = parseFloat(best.v);
  const floorPct = (c) => Math.floor((c * pct) / 100 + 1e-8);
  const off = best.v.includes('%')
    ? (Array.isArray(prices) && prices.length ? prices.reduce((t, c) => t + floorPct(c), 0) : floorPct(subtotal))
    : Math.round(parseFloat(best.v) * 100 * rate);
  return Math.min(Math.max(off || 0, 0), subtotal);
}

const WATCHED = ['data-prices', 'data-subtotal', 'data-next-subtotal', 'data-units', 'data-next-units', 'data-single', 'data-offers', 'data-money-format', 'hidden'];

class SbCurrencyPricing extends HTMLElement {
  connectedCallback() {
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: WATCHED });
    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  update() {
    const { kind, offers, moneyFormat, eachTemplate } = this.dataset;
    const units = Number(this.dataset.units);
    const subtotal = Number(this.dataset.subtotal);
    if (!(units > 0) || !Number.isFinite(subtotal)) return;
    const money = (cents) => formatMoney(cents, moneyFormat);
    const set = (selector, value) => {
      this.querySelectorAll(selector).forEach((node) => {
        if (node.textContent !== value) node.textContent = value;
      });
    };
    // unit prices: the cart's (data-prices, upsell) or count x one price (home cards)
    const now = String(this.dataset.prices || '').split(',').map(Number).filter((n) => n > 0);
    const prices = now.length === units ? now : Array(units).fill(Math.round(subtotal / units));
    const current = subtotal - qtyOff(offers, units, subtotal, prices);
    if (kind === 'home') {
      set('[data-sb-money-total]', money(current));
      set('[data-sb-money-each]', (eachTemplate || '[price]').replace('[price]', money(Math.round(current / units))));
    } else if (kind === 'upsell') {
      const nextUnits = Number(this.dataset.nextUnits) || units + 1;
      const nextSubtotal = Number(this.dataset.nextSubtotal);
      const single = Number(this.dataset.single);
      if (!(nextUnits > units) || !Number.isFinite(nextSubtotal) || !Number.isFinite(single)) return;
      const next = nextSubtotal - qtyOff(offers, nextUnits, nextSubtotal, prices.concat(Array(nextUnits - units).fill(single)));
      const extra = Math.max(0, next - current);
      const saving = single * (nextUnits - units) - extra;
      if (extra <= 0 || saving <= 0) return;
      set('[data-sb-money-extra]', money(extra));
      set('[data-sb-money-saving]', money(saving));
    } else {
      return;
    }
    this.querySelectorAll('[data-sb-price-ready][hidden]').forEach((node) => { node.hidden = false; });
  }
}

if (!customElements.get('sb-currency-pricing')) customElements.define('sb-currency-pricing', SbCurrencyPricing);
